import { useTranslation } from '../i18n/hooks/useTranslation'
import { formatDate } from '../utils/dateUtils'

interface RecentActivityProps {
  recentChallenges: any[]
  recentUsers?: any[]
  showUsers?: boolean
}

export function RecentActivity({ recentChallenges, recentUsers = [], showUsers = false }: RecentActivityProps) {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Challenges */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('dashboard.recentChallenges')}</h2>
          {recentChallenges.length === 0 ? (
            <p className="text-gray-500">{t('dashboard.noRecentChallenges')}</p>
          ) : (
            <div className="space-y-3">
              {recentChallenges.map((challenge: any) => (
                <div key={challenge.id} className="flex items-center text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <span className="text-gray-900">{challenge.title || t('challenges.untitledChallenge')}</span>
                    {challenge.type && (
                      <span className="text-gray-500 ml-2">({challenge.type})</span>
                    )}
                  </div>
                  <span className="text-gray-400">
                    {formatDate(challenge.createdAt, undefined, t)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Users or Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {showUsers ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('analytics.recentUsers')}</h2>
              {recentUsers.length === 0 ? (
                <p className="text-gray-500">{t('analytics.noRecentUsers')}</p>
              ) : (
                <div className="space-y-3">
                  {recentUsers.map((user: any) => (
                    <div key={user.id} className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <span className="text-gray-900">
                          {user.displayName || user.email || t('users.anonymousUser')}
                        </span>
                        {user.role && user.role !== 'user' && (
                          <span className="text-blue-600 ml-2 text-xs font-medium">
                            {t(`users.roles.${user.role}`)}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400">
                        {formatDate(user.createdAt, undefined, t)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('navigation.quickActions')}</h2>
              <div className="space-y-3">
                <a
                  href="/challenges"
                  className="block w-full text-left p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  📝 {t('dashboard.quickActions.manageChallenges')}
                </a>
                <a
                  href="/categories"
                  className="block w-full text-left p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  📂 {t('dashboard.quickActions.manageCategories')}
                </a>
                <a
                  href="/users"
                  className="block w-full text-left p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  👥 {t('dashboard.quickActions.viewUsers')}
                </a>
                <a
                  href="/analytics"
                  className="block w-full text-left p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  📈 {t('dashboard.quickActions.viewAnalytics')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
} 